<template>
  <div
    class="product-card"
    :class="[
      $blockClass(blockClass, 'product-card'),
      { [$class('loading')]: !product },
    ]"
  >
    <div
      class="product-card__images"
      :class="{ 'product-card__images--has-secondary': product && activeProduct.images[1] }"
      :data-product-id="product.id"
    >
      <a
        :href="productUrl"
        :data-product-id="product.id"
      >
        <div v-if="product">
          <div
            v-if="videoSource"
            class="product-card__video"
            :style="{ paddingBottom: videoPaddingBottom }"
          >
            <video-element
              class="product-card__video-element"
              :src="videoSource.url"
              :poster="videoSource.previewImage"
              :alt="videoSource.alt"
              autoplay
              loop
              :showControls="false"
              @click.prevent="redirectToProduct"
            />
          </div>
          <div v-else>
            <responsive-image
              v-if="product && activeProduct.images[0]"
              :key="activeProduct.images[0]"
              class="product-card__image
               product-card__image--primary"
              min-max="300-780"
              :sizes="sizes"
              :image="activeProduct.images[0]"
              :ratio="imageRatio"
            />
            <responsive-image
              v-if="product && activeProduct.images[1]"
              :key="activeProduct.images[1]"
              class="product-card__image product-card__image--secondary"
              min-max="300-780"
              :sizes="sizes"
              :image="activeProduct.images[1]"
              :ratio="imageRatio"
            />
          </div>
        </div>
      </a>

      <div
        class="product-card__quick-add-button-wrapper"
      >
        <quick-add-button
           tabindex="0"
          :active-product="{
            handle: activeProduct.handle,
            id: activeProduct.id,
          }"
          @quick-add-btn-click="updateQuickAdd"
          :aria-label="`Quick add ${activeProduct.title}`"
        />
      </div>

      <Transition name="quick-add-drawer">
        <product-card-quick-add
          v-if="showDesktopQuickAdd"
          :key="product.handle"
          :source="product.handle"
          :handle="quickAddHandle"
          @product-card-quick-add-close="closeDesktopQuickAdd"
          @quick-add-swatch-update="updateActiveSwatch"
        />
      </Transition>

      <span
        v-if="badge"
        class="product-card__badge badge"
        :style="{'color': badgeColor}"
        v-text="badge"
      />

      <div
        ref="wishlistKingObserver"
        class="product-card__wk"
      />
    </div>

    <div
      class="product-card__footer"
      :class="{
        'has-swatches': cleanSwatches.length,
        'visually-hidden': hideProductDetails,
      }"
    >
      <ul
        v-if="cleanSwatches.length && !showDesktopQuickAdd"
        class="product-card__swatches"
      >
        <li
          v-for="(swatch, swatchIndex) of cleanSwatches.slice(0, swatchLimit)"
          :key="swatchIndex"
          class="product-card__swatch"
        >
          <a
            tabindex="0"
            class="product-card__swatch-link"
            :class="{
              'product-card__swatch-link--missing': !swatch.loaded,
              'product-card__swatch-link--active': swatch.active,
              'product-card__swatch-link--white': swatch.key.includes('white')
            }"
            :title="`${swatch.title}`"
            @click="activateSwatch(swatch)"
            @keyup.enter="activateSwatch(swatch)"
            :aria-current="swatch.active ? 'true' : 'false'"
          >
            <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
            <img
              class="product-card__swatch-image"
              :src="getSwatchFile(swatch.swatch)"
              alt=""
              @load="swatchLoaded(swatch)"
            >
          </a>
        </li>
        <li
          v-if="cleanSwatches.length > swatchLimit"
          class="product-card__swatch-caption caption"
        >
          <a
            :href="productUrl"
          >
            <icon-plus />
          </a>
        </li>
      </ul>

      <quick-add-button-mobile
         tabindex="0"
        :active-product="{
          handle: activeProduct.handle,
          id: activeProduct.id,
        }"
        @quick-add-btn-click="updateQuickAdd"
        :aria-label="`Quick add ${activeProduct.title}`"
      />

      <div class="product-card__footer-text">
        <span
          v-if="isPreorder"
          class="product-card__preorder-badge badge"
          v-html="$string('collection.preorder')"
        />

        <a
          :href="productUrl"
          class="product-card__title body-2"
          tabindex="0"
          v-text="getProductTitle"
        />

        <product-prices
          class="product-card__prices body-2"
          :available="activeProduct?.available"
          :compare-at-price="activeProduct?.compareAtPrice?.amount"
          :price="activeProduct?.price?.amount"
          :message="messageTag"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Number} index - Product card's forloop index.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Object} swatches - Colors and corresponding swatch images.
 *
 */
import { mapActions, mapState } from 'vuex'
import { values as breakpoints } from '~/config/breakpoints'
import { handleize } from '~/helpers/general'
import ResponsiveImage from '~global/images/responsive-image'
import ProductPrices from '~global/product-prices/product-prices'
import IconPlus from '~icons/misc/plus.svg'
import QuickAddButton from './quick-add-button'
import QuickAddButtonMobile from './quick-add-button-mobile'
import ProductCardQuickAdd from './product-card-quick-add'
import VideoElement from '~global/video-element/video-element'

export default {
  name: 'ProductCard',

  components: {
    IconPlus,
    ProductPrices,
    ResponsiveImage,
    QuickAddButton,
    QuickAddButtonMobile,
    ProductCardQuickAdd,
    VideoElement,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    hideProductDetails: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    wishlist: {
      type: Boolean,
      default: false,
    },
    imageRatio: {
      type: String,
      default: '118:177',
    },
    videoRatio: {
      type: String,
      default: '118:177',
    },
    collection: {
      type: String,
      default: '',
    },
    hasVideo: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'quick-add-product-updated': (payload) => typeof payload === 'string',
    'product-card-quick-add-open': () => true,
    'product-card-quick-add-close': () => true,
  },

  data() {
    return {
      activeProduct: {},
      breakpoints,
      cleanSwatches: [],
      swatchLimit: 3,
      quickAddHandle: '',
      showDesktopQuickAdd: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      swatches: (state) => state.index.swatches,
      isMobile: (state) => state.index.screen.mobile,
      activeOverlay: (state) => state.overlays.active,
    }),

    /**
     * Compute product title.
     * @returns {String}
     */
    getProductTitle() {
      if (!this.activeProduct) {
        return ''
      }

      let title = this.activeProduct.title

      if (title.includes(' - ')) {
        title = title.split(' -')[0]
      }

      return title
    },

    /**
     * Get badge text from product tags
     */
    badge() {
      if (!this.activeProduct.tags) {
        return false
      }

      const badge = this.activeProduct.tags.find((tag) => {
        const data = tag.split(':')
        return data[0] === 'badge'
      })

      if (!badge) {
        return false
      }

      const badgeData = badge.split(':')
      const badgeText = badgeData[badgeData.length - 1]

      return badgeText
    },

    /**
     * Compute badge color from product tags.
     */
    badgeColor() {
      if (!this.activeProduct.tags) {
        return '#000000'
      }

      const color = this.activeProduct.tags.find((data) => data.includes('badge-color:'))

      if (!color) {
        return '#000000'
      }

      return color.split(':')[1]
    },

    /**
     * Compute video padding bottom.
     * @returns {String}
     */
    videoPaddingBottom() {
      const [width, height] = this.videoRatio.split(':')
      return `${(height / width) * 100}%`
    },

    /**
     * Compute video source.
     * @returns {Object}
     */
    videoSource() {
      if (
        !this.hasVideo ||
        !this.activeProduct.media ||
        !this.activeProduct.media.edges ||
        this.activeProduct.media.edges.length <= 2
      ) {
        return null
      }

      const videoNode = this.activeProduct.media.edges[0]?.node
      if (!videoNode || videoNode.mediaType !== 'VIDEO') {
        return null
      }

      const source = videoNode.sources.find((src) => {
        return src.format === 'mp4' && src.height === 480
      })

      if (!source) {
        return null
      }

      return {
        url: source.url,
        alt: videoNode.alt,
        previewImage: videoNode.previewImage.url,
      }
    },

    /**
     * Compute if this is a pre-order product.
     * @returns {Boolean}
     */
    isPreorder() {
      if (!this.product) {
        return false
      }

      const preorderTag = this.product.tags.find((tag) => {
        const clean = tag.toLowerCase()
        return clean === 'preorder'
      })

      if (preorderTag) {
        return true
      }

      return false
    },

    /**
     * Compute product URL with root route included.
     * @returns {String}
     */
    productUrl() {
      let root = window.cnvs.routes.root
      if (root !== '/') {
        root = `${root}/`
      }

      let collection = ''
      if (this.collection) {
        collection = `collections/${this.collection}/`
      }

      return `${root}${collection}products/${this.activeProduct.handle}`
    },

    /**
     * Compute message from product tags
     * @returns {Boolean, String}
     */
    messageTag() {
      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => data.includes('message:'))

      if (!tag) {
        return false
      }

      return tag.split(':')[1]
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}) 34vw, 100vw`
    },
  },

  watch: {

    /**
     * Wait for global swatches array to be ready, then set swatches.
     */
    swatches(data) {
      this.getAllSwatches(data)
    },

    /**
     * Watch for active overlay to close quick add.
     */
    activeOverlay() {
      if (!this.showDesktopQuickAdd) {
        return
      }

      this.closeDesktopQuickAdd()
    },
  },

  mounted() {
    if (this.swatches.length) {
      this.getAllSwatches(this.swatches)
    }

    if (this.wishlist) {
      this.refreshWishlistKing(this.product.id)
    }

    cnvs.EventBus.listen('activateSwatch', (data) => {
      const { swatch, source } = data

      if (source === this.product.handle) {
        this.activateSwatch(swatch)
      }
    })
  },

  created() {
    this.activeProduct = JSON.parse(JSON.stringify(this.product))
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Redirect to the product PDP page.
     */
    redirectToProduct() {
      window.location.href = this.productUrl;
    },

    /**
     * Filter global swatches object by product title.
     * @returns {Object}
     */
    getSwatchesByTitle(array) {
      if (!this.product) {
        return {}
      }

      const cleanTitle = this.product.title.split(' - ')[0]
      const swatches = array.filter(swatch => {
        return swatch.parentTitle === cleanTitle
      })

      return swatches
    },

    /**
     * Update the active swatch triggered from quick add
     * @param {Object} product - product update from quick add
     */
    updateActiveSwatch(product) {
      const newSwatch = this.cleanSwatches.find((swatch) => {
        return swatch.handle === product.handle && !swatch.active
      })

      if (newSwatch) {
        this.activateSwatch(newSwatch)
      }
    },

    /**
     * Open.
     * @param {Object} response - The event data
     */
    updateQuickAdd(response) {
      if (this.isMobile) {
        this.toggleOverlay({
          ignoreDismissed: true,
          namespace: 'quickAdd',
        })

        cnvs.EventBus.emit('quick-add:update-handle', {
          handle: response,
          source: this.product.handle,
        })

        return
      }

      this.openDesktopQuickAdd()
    },

    /**
     * Open desktop quick add.
     */
    openDesktopQuickAdd() {
      this.quickAddHandle = this.activeProduct?.handle || this.product.handle
      this.showDesktopQuickAdd = true

      cnvs.EventBus.emit('product-card-quick-add:close', {
        source: this.product.handle,
      })
      this.$emit('product-card-quick-add-open')
    },

    /**
     * Close desktop quick add.
     */
    closeDesktopQuickAdd() {
      this.showDesktopQuickAdd = false
      this.$emit('product-card-quick-add-close')
    },

    /**
     * Get swatch image from Shopify files.
     * @param {String} file - image file name
     * @returns {String}
     */
    getSwatchFile(file) {
      const location = window.cnvs.urls.files.split('placeholder')[0]
      return `${location}${file}`
    },

    /**
     * Set swatch state to loaded.
     * @param {Object} swatch
     */
    swatchLoaded(swatch) {
      swatch.loaded = true
    },

    /**
     * Delete and re-render WishlistKing button using WishlistKing SDK.
     * TODO: Add wishlist king functionality
     */
    // eslint-disable-next-line no-unused-vars
    refreshWishlistKing(id) {
      this.$refs.wishlistKingObserver.innerHTML = ''
      // this.$refs.wishlistKingObserver.prepend(
      //   // eslint-disable-next-line no-undef
      //   WishlistKing.createComponent('wishlist-button-floating', {
      //     id,
      //   }),
      // )
    },

    /**
     * Sort swatches so the first one represents the active product.
     */
    sortSwatches() {
      if (!this.product) {
        return
      }

      const data = this.product.title.split(' - ')
      const color = handleize(data[data.length - 1])
      const activeSwatch = this.cleanSwatches.find((swatch) => swatch.key === color)
      const index = this.cleanSwatches.indexOf(activeSwatch)

      if (index > 0) {
        this.cleanSwatches.splice(index, 1)
        this.cleanSwatches.unshift(activeSwatch)
      }

      if (!this.cleanSwatches || !this.cleanSwatches.length) {
        return
      }

      this.cleanSwatches[0].active = true
    },

    /**
     * Deactivate currently activated swatch, activate new swatch.
     * @param {Object} newSwatch
     */
    activateSwatch(newSwatch) {
      const current = this.cleanSwatches.find((swatch) => swatch.active)
      const updatedSwatch = this.cleanSwatches.find((swatch) => {
        return newSwatch.id === swatch.id
      })

      current.active = false
      updatedSwatch.active = true

      this.activeProduct = updatedSwatch
      this.refreshWishlistKing(this.activeProduct.id)

      this.quickAddHandle = this.activeProduct.handle
    },

    /**
     * Get all swatches before filtering.
     * @param {Array} array - Array of swatches.
     */
    getAllSwatches(array) {
      const swatches = this.getSwatchesByTitle(array)

      this.cleanSwatches = JSON.parse(JSON.stringify(swatches))
      this.sortSwatches()
    },
  },
}
</script>

<style lang="scss">
@import './product-card';

.quick-add-drawer-enter-active,
.quick-add-drawer-leave-active {
  transition: all 500ms ease-out;
  transform: translateY(0);
}

.quick-add-drawer-enter-from,
.quick-add-drawer-leave-to {

  transform: translateY(500px);
}
</style>
